import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CourseStructure, MainContent, ToolsPanel } from '../components/layout';
import { LoadingSpinner } from '../components/ui';
import { useLocalStorage } from '../hooks';
import { useAudioStore } from '../stores';
import '../styles/MainContent.css';

const ReadingPage = ({ courseData: propCourseData, pdfData: propPdfData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [textSize, setTextSize] = useLocalStorage('textSize', 'normal');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const audioRef = useRef(null);

  // Audio store
  const { initialize: initializeAudio, loadPagePlaylist } = useAudioStore();

  // Load data from props, location state, or session storage
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        let data = null;

        // Priority: props > location state > session storage
        if (propCourseData && propPdfData) {
          data = {
            courseData: propCourseData,
            pdfData: propPdfData
          };
        } else if (location.state?.topic && location.state?.courseId) {
          // Coming from CourseDetailsPage
          data = {
            courseData: { topics: [location.state.topic] },
            pdfData: location.state.topic
          };
        } else {
          // Try to load from session storage (from TopicsPage)
          const sessionData = sessionStorage.getItem('readingPageData');
          if (sessionData) {
            data = JSON.parse(sessionData);
          }
        }

        if (!data || !data.pdfData) {
          setError('No PDF data available');
          return;
        }

        // Process the data
        const processedCourseData = data.courseData || {
          id: 'single-pdf',
          title: data.pdfData.title,
          topics: [data.pdfData]
        };

        // Ensure topics have proper structure
        const processedTopics = processedCourseData.topics.map((topic, index) => ({
          ...topic,
          id: topic.id || `topic-${index}`,
          title: topic.title || `Topic ${index + 1}`,
          content: topic.content || {
            heading: topic.title || `Topic ${index + 1}`,
            body: topic.body || []
          },
          currentPageIndex: topic.currentPageIndex || 0,
          originalStructure: topic.originalStructure || {
            pageId: `page-0`,
            totalPages: 1,
            topicId: topic.id || `topic-${index}`,
            subTopicId: topic.subTopicId || 'default'
          }
        }));

        const finalCourseData = {
          ...processedCourseData,
          topics: processedTopics
        };

        setCourseData(finalCourseData);

        // Set the first topic as active by default
        const firstTopic = processedTopics[0];
        if (firstTopic) {
          setCurrentTopic(firstTopic);
          setActiveTopicId(firstTopic.id);

          // Initialize audio if available
          if (firstTopic.audioSources && firstTopic.audioSources.length > 0) {
            loadPagePlaylist(firstTopic.id, firstTopic.audioSources);
          }
        }

      } catch (err) {
        console.error('Error loading reading page data:', err);
        setError('Failed to load content');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [propCourseData, propPdfData, location.state, loadPagePlaylist]);

  // Initialize audio store
  useEffect(() => {
    const cleanup = initializeAudio(audioRef);
    return cleanup;
  }, [initializeAudio]);

  // Handle topic selection
  const handleSelectTopic = useCallback((topicId) => {
    if (!courseData) return;

    const topic = courseData.topics.find(t => t.id === topicId);
    if (topic) {
      setCurrentTopic(topic);
      setActiveTopicId(topicId);

      // Load audio playlist for the new topic
      if (topic.audioSources && topic.audioSources.length > 0) {
        loadPagePlaylist(topic.id, topic.audioSources);
      }
    }
  }, [courseData, loadPagePlaylist]);

  // Handle page navigation within a topic
  const handlePageChange = useCallback((topicId, pageNumber) => {
    if (!courseData) return;

    const topic = courseData.topics.find(t => t.id === topicId);
    if (topic) {
      const updatedTopic = {
        ...topic,
        currentPageIndex: pageNumber - 1, // Convert to 0-based index
        originalStructure: {
          ...topic.originalStructure,
          pageId: `page-${pageNumber - 1}`
        }
      };

      // Update the topic in courseData
      const updatedTopics = courseData.topics.map(t =>
        t.id === topicId ? updatedTopic : t
      );

      const updatedCourseData = {
        ...courseData,
        topics: updatedTopics
      };

      setCourseData(updatedCourseData);
      setCurrentTopic(updatedTopic);
    }
  }, [courseData]);

  // Handle navigation to specific page (from bookmarks)
  const handleNavigateToPage = useCallback((topicId, pageId) => {
    if (!courseData) return;

    const topic = courseData.topics.find(t => t.id === topicId);
    if (topic) {
      // Extract page number from pageId (assuming format like "page-0")
      const pageMatch = pageId.match(/page-(\d+)/);
      const pageIndex = pageMatch ? parseInt(pageMatch[1], 10) : 0;

      handlePageChange(topicId, pageIndex + 1); // Convert to 1-based for handlePageChange
      handleSelectTopic(topicId);
    }
  }, [courseData, handlePageChange, handleSelectTopic]);

  // Handle text size change
  const handleTextSizeChange = useCallback((size) => {
    setTextSize(size);
  }, [setTextSize]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Clear session storage
    sessionStorage.removeItem('readingPageData');

    // Navigate back based on how we got here
    if (location.state?.courseId) {
      navigate(`/course/${location.state.courseId}`);
    } else {
      navigate('/dashboard');
    }
  }, [navigate, location.state]);

  // Loading state
  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading-container">
          <LoadingSpinner size="large" message="Loading PDF..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="app-container">
        <div className="error-container">
          <h2>{error}</h2>
          <button onClick={handleBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!courseData || !currentTopic) {
    return (
      <div className="app-container">
        <div className="error-container">
          <h2>No content available</h2>
          <button onClick={handleBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Course Structure Sidebar */}
      <CourseStructure
        topics={courseData.topics}
        activeTopicId={activeTopicId}
        onSelectTopic={handleSelectTopic}
        courseData={courseData}
        onNavigateToPage={handleNavigateToPage}
      />

      {/* Main Content Area */}
      <MainContent
        topic={currentTopic}
        textSize={textSize}
      />

      {/* Tools Panel Sidebar */}
      <ToolsPanel
        activeTextSize={textSize}
        onSetTextSize={handleTextSizeChange}
        currentTopic={currentTopic}
        onPageChange={handlePageChange}
      />

      {/* Hidden Audio Element for Audio Store */}
      <audio
        ref={audioRef}
        id="global-audio-player"
        style={{ display: 'none' }}
        preload="metadata"
      />
    </div>
  );
};

export default ReadingPage;